// frontend/src/pages/ClubesMilhasPage/ClubesMilhasPage.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Button, Table, message, Space, Popconfirm, Tag, Tooltip, Row, Col, List, Card, Divider, Alert } from 'antd';
import { PlusOutlined, EditOutlined, StopOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getClubesAssinatura, cancelarClubeAssinatura, deleteClubeAssinatura, reativarClubeAssinatura } from '../../services/milhasService';
import ClubeFormModal from '../../components/ClubeFormModal/ClubeFormModal';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import { useDataRefresh } from '../../contexts/DataRefreshContext';
import { getErrorMessage } from '../../utils/errorHandler';

const { Title, Text } = Typography;

const BANNER_DISMISS_KEY = 'sa_clubes_termino_banner_dismissed';

const getTerminoUrgencia = (clube) => {
    if (!clube?.data_termino_planejada || clube.status !== 'ATIVO') return null;
    const hoje = dayjs().startOf('day');
    const data = dayjs(clube.data_termino_planejada).startOf('day');
    if (!data.isValid()) return null;
    if (data.isBefore(hoje)) {
        return { color: 'red', label: 'Término planejado vencido' };
    }
    if (data.diff(hoje, 'day') <= 7) {
        return { color: 'orange', label: 'Revisar em breve' };
    }
    return null;
};

const formatTermino = (value) => {
    if (!value) return 'Sem lembrete';
    const d = dayjs(value);
    return d.isValid() ? d.format('DD/MM/YYYY') : 'Sem lembrete';
};

const ClubesMilhasPage = () => {
    const [clubes, setClubes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedClube, setSelectedClube] = useState(null);
    const [bannerDismissed, setBannerDismissed] = useState(() => {
        try {
            return localStorage.getItem(BANNER_DISMISS_KEY) === '1';
        } catch {
            return false;
        }
    });

    const [messageApi, contextHolder] = message.useMessage();
    const { refreshKey, triggerRefresh } = useDataRefresh();

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchClubes = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getClubesAssinatura();
            setClubes(res.data);
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao buscar a lista de clubes.'));
        } finally {
            setLoading(false);
        }
    }, [messageApi]);

    useEffect(() => {
        fetchClubes();
    }, [fetchClubes, refreshKey]);

    const showTerminoBanner = useMemo(() => {
        if (bannerDismissed) return false;
        return clubes.some((c) => c.status === 'ATIVO' && !c.data_termino_planejada);
    }, [clubes, bannerDismissed]);

    const dismissBanner = () => {
        try {
            localStorage.setItem(BANNER_DISMISS_KEY, '1');
        } catch {
            /* ignore */
        }
        setBannerDismissed(true);
    };

    const handleOpenModal = (clube = null) => {
        setSelectedClube(clube);
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setSelectedClube(null);
    };

    const handleSaveSuccess = (shouldClose = true, successMsg = null) => {
        if (successMsg) messageApi.success(successMsg);
        triggerRefresh();
        if (shouldClose) {
            handleCloseModal();
        }
    };

    const handleCancelarClube = async (clubeId) => {
        try {
            await cancelarClubeAssinatura(clubeId);
            messageApi.success('Clube cancelado com sucesso!');
            triggerRefresh();
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao cancelar o clube.'));
        }
    };

    const handleReativarClube = async (clubeId) => {
        try {
            await reativarClubeAssinatura(clubeId);
            messageApi.success('Clube reativado com sucesso!');
            triggerRefresh();
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao reativar o clube.'));
        }
    };

    const handleDeleteClube = async (clubeId) => {
        try {
            await deleteClubeAssinatura(clubeId);
            messageApi.success('Clube excluído com sucesso!');
            triggerRefresh();
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao excluir o clube.'));
        }
    };

    const renderTerminoCell = (record) => {
        const urgencia = getTerminoUrgencia(record);
        return (
            <Space direction="vertical" size={0}>
                <Tooltip title="Lembrete opcional. Não cancela o clube automaticamente.">
                    <Text type={record.data_termino_planejada ? undefined : 'secondary'}>
                        {formatTermino(record.data_termino_planejada)}
                    </Text>
                </Tooltip>
                {urgencia && <Tag color={urgencia.color}>{urgencia.label}</Tag>}
            </Space>
        );
    };

    const columns = [
        {
            title: 'Plano',
            dataIndex: 'plano_clube',
            key: 'plano_clube',
        },
        {
            title: 'Programa / Portador',
            dataIndex: 'programa_portador_display',
            key: 'programa_portador_display',
        },
        {
            title: 'Pontos Mensais',
            dataIndex: 'pontos_mensais',
            key: 'pontos_mensais',
            render: (val) => parseFloat(val).toLocaleString('pt-BR'),
        },
        {
            title: 'Valor Mensal',
            dataIndex: 'valor_mensal',
            key: 'valor_mensal',
            render: (val) => parseFloat(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        },
        {
            title: 'Dia do Crédito',
            dataIndex: 'dia_credito_mensal',
            key: 'dia_credito_mensal',
        },
        {
            title: 'Término planejado',
            key: 'data_termino_planejada',
            render: (_, record) => renderTerminoCell(record),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color={status === 'ATIVO' ? 'green' : 'red'}>{status}</Tag>,
        },
        {
            title: 'Ações',
            key: 'acoes',
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Editar">
                        <Button icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
                    </Tooltip>

                    {record.can_delete && (
                         <Popconfirm
                            title="Excluir o clube?"
                            description="Esta ação é permanente. Confirma?"
                            onConfirm={() => handleDeleteClube(record.id)}
                            okText="Sim, Excluir"
                            cancelText="Não"
                        >
                            <Tooltip title="Excluir">
                                <Button danger icon={<DeleteOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    )}

                    {record.status === 'ATIVO' && (
                        <Popconfirm
                            title="Cancelar a assinatura?"
                            description="A projeção futura será removida. Confirma?"
                            onConfirm={() => handleCancelarClube(record.id)}
                            okText="Sim"
                            cancelText="Não"
                        >
                            <Tooltip title="Cancelar">
                                <Button danger icon={<StopOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    )}

                    {record.status === 'INATIVO' && (
                        <Popconfirm
                            title="Reativar a assinatura?"
                            description="Uma nova projeção será criada para o próximo mês. Confirma?"
                            onConfirm={() => handleReativarClube(record.id)}
                            okText="Sim, Reativar"
                            cancelText="Não"
                        >
                            <Tooltip title="Reativar">
                                <Button icon={<CheckCircleOutlined />} style={{ color: 'green' }} />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const getMobileActions = (record) => {
        const actions = [];

        actions.push(
            <span key="edit" onClick={() => handleOpenModal(record)}>
                <EditOutlined /> Editar
            </span>
        );

        if (record.status === 'ATIVO') {
            actions.push(
                <Popconfirm
                    title="Cancelar?"
                    description="Confirma o cancelamento?"
                    onConfirm={() => handleCancelarClube(record.id)}
                    okText="Sim"
                    cancelText="Não"
                    key="cancel"
                >
                    <span style={{ color: '#faad14' }}>
                        <StopOutlined /> Cancelar
                    </span>
                </Popconfirm>
            );
        } else if (record.status === 'INATIVO') {
            actions.push(
                <Popconfirm
                    title="Reativar?"
                    description="Confirma reativação?"
                    onConfirm={() => handleReativarClube(record.id)}
                    okText="Sim"
                    cancelText="Não"
                    key="reactivate"
                >
                    <span style={{ color: '#52c41a' }}>
                        <CheckCircleOutlined /> Reativar
                    </span>
                </Popconfirm>
            );
        }

        if (record.can_delete) {
            actions.push(
                <Popconfirm
                    title="Excluir?"
                    description="Ação permanente."
                    onConfirm={() => handleDeleteClube(record.id)}
                    okText="Sim"
                    cancelText="Não"
                    key="delete"
                >
                    <span style={{ color: '#ff4d4f' }}>
                        <DeleteOutlined /> Excluir
                    </span>
                </Popconfirm>
            );
        }

        return actions;
    };

    return (
        <div style={{ padding: isMobile ? '16px' : '0' }}>
            {contextHolder}

            <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12}>
                    <Title level={2} style={{ margin: 0 }}>Gestão de Clubes</Title>
                </Col>
                <Col xs={24} sm={12} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => handleOpenModal()}
                        block={isMobile}
                        size={isMobile ? 'large' : 'middle'}
                    >
                        Adicionar Novo Clube
                    </Button>
                </Col>
            </Row>

            {showTerminoBanner && (
                <Alert
                    type="info"
                    showIcon
                    closable
                    onClose={dismissBanner}
                    style={{ marginBottom: 16 }}
                    message="Data de término planejada"
                    description="Você pode definir uma data de término planejada para lembrar de cancelar ou renegociar o clube — isso não encerra automaticamente. Edite um clube ativo para informar a data."
                />
            )}

            {(() => {
                const emptyClubes = (
                    <EmptyStateGuide
                        title="Nenhum clube cadastrado."
                        description="Cadastre assinaturas de clubes de milhas para projetar créditos mensais e acompanhar o status."
                        actionLabel="Adicionar novo clube"
                        onAction={() => handleOpenModal()}
                    />
                );
                return isMobile ? (
                <List
                    dataSource={clubes}
                    loading={loading}
                    rowKey="id"
                    locale={{ emptyText: emptyClubes }}
                    renderItem={(item) => {
                        const urgencia = getTerminoUrgencia(item);
                        return (
                        <Card
                            style={{ marginBottom: 16 }}
                            actions={getMobileActions(item)}
                            bodyStyle={{ padding: '16px' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                <div>
                                    <Text strong style={{ fontSize: '1.1rem' }}>{item.plano_clube}</Text>
                                    <div style={{ marginTop: 4, color: '#666', fontSize: '0.9rem' }}>
                                        {item.programa_portador_display}
                                    </div>
                                </div>
                                <Tag color={item.status === 'ATIVO' ? 'success' : 'error'} icon={item.status === 'ATIVO' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                                    {item.status}
                                </Tag>
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            <Row gutter={[16, 12]}>
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Pontos Mensais</Text>
                                    <div style={{ fontWeight: 500 }}>{parseFloat(item.pontos_mensais).toLocaleString('pt-BR')}</div>
                                </Col>
                                <Col span={12} style={{ textAlign: 'right' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Valor Mensal</Text>
                                    <div style={{ fontWeight: 500, color: '#52c41a' }}>
                                        {parseFloat(item.valor_mensal).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Dia do Crédito</Text>
                                    <div style={{ fontWeight: 500 }}>Todo dia {item.dia_credito_mensal}</div>
                                </Col>
                                <Col span={12} style={{ textAlign: 'right' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Término planejado</Text>
                                    <div style={{ fontWeight: 500 }}>{formatTermino(item.data_termino_planejada)}</div>
                                    {urgencia && (
                                        <Tag color={urgencia.color} style={{ marginTop: 4 }}>{urgencia.label}</Tag>
                                    )}
                                </Col>
                            </Row>
                        </Card>
                        );
                    }}
                />
            ) : (
                <Table
                    columns={columns}
                    dataSource={clubes}
                    loading={loading}
                    rowKey="id"
                    pagination={{ pageSize: 25 }}
                    locale={{ emptyText: emptyClubes }}
                />
            );
            })()}

            {isModalVisible && (
                <ClubeFormModal
                    visible={isModalVisible}
                    onClose={handleCloseModal}
                    onSave={handleSaveSuccess}
                    clube={selectedClube}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
};

export default ClubesMilhasPage;
